import { AppCard, AppPage } from '../../components/general';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { FeeConfigurationDetails } from './FeeConfigurationDetails';
import { useFeeConfiguration, useFeeConfigurations, useHudLines, useHudLine, useFeeVersions } from '../../hooks/useFees';

export const FeeConfigurationDetailsContainer = () => {
  const { feeConfigurationId: routeId } = useParams<{ feeConfigurationId: string }>();
  const [searchParams] = useSearchParams();
  const feeConfigurationId = routeId ? parseInt(routeId, 10) : undefined;
  const isCreateMode = !feeConfigurationId;
  
  const versionIdFromQuery = useMemo(() => {
    const versionIdParam = searchParams.get('versionId');
    if (versionIdParam) {
      const versionId = parseInt(versionIdParam, 10);
      return !isNaN(versionId) ? versionId : null;
    }
    return null;
  }, [searchParams]);
  
  const { user, isLoading: authIsLoading } = useAuth();
  const { feeConfigurationQuery, updateFeeConfiguration } = useFeeConfiguration(feeConfigurationId);
  const { feeConfigurationsQuery, createFeeConfiguration } = useFeeConfigurations();
  const { hudLinesQuery } = useHudLines(null);
  const { versionsQuery } = useFeeVersions();
  const currentHudLineId = feeConfigurationQuery.data?.feeHudLineId;
  const { hudLineQuery: currentHudLineQuery } = useHudLine(currentHudLineId);

  const isLoading =
    authIsLoading ||
    !user ||
    (isCreateMode ? false : feeConfigurationQuery.isLoading) ||
    feeConfigurationsQuery.isLoading ||
    hudLinesQuery.isLoading ||
    versionsQuery.isLoading ||
    (currentHudLineId !== undefined && currentHudLineQuery.isLoading);

  const allHudLines = useMemo(() => {
    const drafts = hudLinesQuery.data ?? [];
    const current = currentHudLineQuery.data;
    
    if (current) {
      const existsInDrafts = drafts.some((h) => h.feeHudLineId === current.feeHudLineId);
      if (!existsInDrafts) {
        return [current, ...drafts];
      }
    }
    
    return drafts;
  }, [hudLinesQuery.data, currentHudLineQuery.data]);

  if (isLoading) {
    return (
      <AppPage title={isCreateMode ? "Create Fee Configuration" : "Fee Configuration Details"} breadcrumbs="Admin - Fees - Details">
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (!isCreateMode && !feeConfigurationQuery.data) {
    return (
      <AppPage title="Fee Configuration Details" breadcrumbs="Admin - Fees - Details">
        <AppCard>
          <div>Fee configuration not found</div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <FeeConfigurationDetails
      feeConfiguration={isCreateMode ? null : feeConfigurationQuery.data!}
      allFeeConfigurations={feeConfigurationsQuery.data ?? []}
      hudLines={allHudLines}
      versions={versionsQuery.data ?? []}
      saveFeeConfiguration={isCreateMode ? createFeeConfiguration : updateFeeConfiguration}
      isCreateMode={isCreateMode}
      versionIdFromQuery={versionIdFromQuery}
    />
  );
};

